import {
  Box,
  Button,
  ButtonGroup,
  Icon,
  List,
  ListItem,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { FlowType, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { NewJoinSessionData } from '../../application/models/NewJoinSessionData';
import { benefitListTitleMap, promotionMap } from '../../utils/stringHelper';
import { numberHelper } from '../../utils/numberHelper';
import { IAdditionalBenefits } from '@lib/types';
import { IconName } from '@ace-clublabs/shared-component-library/dist/components/Icons';
import { PillTag } from './PillTag';
import { promotionIconMap } from '../../utils/helpers';

export interface LocationState {
  isUpgrading?: boolean;
  redirectPath?: string;
  previousMembershipLevel: MembershipLevel;
}

interface IMembershipBenefitProps {
  sessionData: NewJoinSessionData;
  additionalBenefits: IAdditionalBenefits | undefined;
  membershipLevel: MembershipLevel;
  onContinue?: () => void;
  onShowBenefitsTable?: () => void;
  isUpgrading?: boolean;
  admissionFee: number;
  admissionDiscount: number;
  allowedMembershipLevels?: MembershipLevel[] | undefined;
}

export const MembershipBenefit = ({
  sessionData,
  additionalBenefits,
  membershipLevel: selectedMembershipLevel,
  onContinue,
  onShowBenefitsTable,
  isUpgrading,
  admissionFee,
  admissionDiscount,
  allowedMembershipLevels,
}: IMembershipBenefitProps) => {
  const sections = [additionalBenefits?.['group1'], additionalBenefits?.['group2']].filter(Boolean);

  const previousMembershipLevel = sessionData.formData.membershipLevel ?? MembershipLevel.CLASSIC;
  const hasAdmissionDiscount = admissionDiscount > 0;
  const admissionFeeAfterDiscount = hasAdmissionDiscount && admissionFee - admissionDiscount;
  const admissionWaived = admissionFee === admissionDiscount;
  const isGrouponFlow = sessionData.flowType === FlowType.GROUPON;
  const iconsNames = ['truck-duotone', 'circle-plus-duotone'];

  const labels = allowedMembershipLevels?.map((memLevel) => benefitListTitleMap[memLevel]) || [];

  const largestLength = Math.max(...labels.map((str) => str.length));
  const largestLabel = labels.find((label) => label.length === largestLength);

  const benefitTitle =
    labels.length > 1 ? (
      <Typography
        variant="b1Bold"
        className="benefit-list-title"
        sx={{ position: 'relative', display: 'inline-block' }}
      >
        <Box component="span" sx={{ position: 'absolute', top: 0, left: 0 }}>
          {isGrouponFlow ? benefitListTitleMap[MembershipLevel.CLASSIC] : benefitListTitleMap[selectedMembershipLevel]}
        </Box>
        <Box component="span" sx={{ visibility: 'hidden', opacity: 0 }}>
          {largestLabel}
        </Box>
      </Typography>
    ) : (
      <Typography variant="b1Bold" className="benefit-list-title">
        {isGrouponFlow ? benefitListTitleMap[MembershipLevel.CLASSIC] : benefitListTitleMap[selectedMembershipLevel]}
      </Typography>
    );

  const sectionListFragment = (
    <>
      {benefitTitle}
      {sections.map((section, index) => (
        <span key={index}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: '500', color: '#0C7A93', marginBottom: '0.5rem' }}
            className={`section${index + 1}-title`}
          >
            <Icon
              iconName={iconsNames[index] as IconName}
              sx={{ fontSize: '1.25rem', marginTop: '0.25rem', marginRight: '0.25rem' }}
            ></Icon>

            {section?.title}
          </Typography>
          <List sx={{ marginTop: '-0.25rem' }}>
            {section?.benefits.map((benefit: any, i: any) => (
              <ListItem sx={{ marginLeft: '0.35rem' }} className="benefit-item-container" key={'ben-' + i}>
                <Typography component={'span'} sx={{ alignSelf: 'baseline' }}>
                  {'\u2022'}
                </Typography>
                <Typography component={'span'} variant="b2Regular">
                  <span dangerouslySetInnerHTML={{ __html: benefit }} />
                </Typography>
              </ListItem>
            ))}
          </List>
        </span>
      ))}
    </>
  );

  return (
    <Stack className="benefit-card-and-description" flexDirection="column" gap="0.5rem" width={'100%'}>
      <Stack
        className="admission-fee-container"
        direction="row"
        sx={(theme) => ({
          alignSelf: 'self-start',
          marginTop: '1rem',
          [theme.breakpoints.up('lg')]: {
            alignSelf: 'center',
            marginTop: 0,
          },
        })}
      >
        {admissionFee ? (
          <Typography id="admission-fee" variant="b1Regular">
            + One-time admission fee:{' '}
            <Typography
              component="span"
              className={hasAdmissionDiscount ? 'strikethrough' : 'admission-fee'}
              marginRight={'0.2rem'}
            >
              {numberHelper.formatMoney(admissionFee)}{' '}
            </Typography>
            {hasAdmissionDiscount && (
              <Typography component="span" fontWeight={700} color="#7527B9">
                {admissionWaived ? 'Waived' : numberHelper.formatMoney(admissionFeeAfterDiscount as number)}
              </Typography>
            )}
          </Typography>
        ) : (
          <></>
        )}
      </Stack>
      <ButtonGroup
        className="benefit-buttons"
        width="100%"
        paddingTop="1rem"
        paddingBottom="1rem"
        gap="1rem !important"
      >
        <Button
          label={getBenefitButtonText(selectedMembershipLevel, Boolean(isUpgrading), previousMembershipLevel)}
          fullWidth
          onClick={onContinue}
          variant="contained"
          size="medium"
          id="getMembershipButton"
        />
        <Button
          label={getFullBenefitButtonText(selectedMembershipLevel)}
          fullWidth
          onClick={() => {
            if (onShowBenefitsTable) {
              onShowBenefitsTable();
            } else {
              document.getElementById('benefits-table-header')?.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          variant="outlined"
          size="medium"
          id="fullBenefitButton"
        />
      </ButtonGroup>
      <Box sx={{ alignSelf: 'flex-end' }}>
        <PillTag
          content={
            <Stack direction="row" alignItems="center" gap="0.25rem">
              {promotionIconMap(selectedMembershipLevel)}
              <Typography variant="b1Medium" sx={{ color: 'inherit' }}>
                {promotionMap[selectedMembershipLevel].text}
              </Typography>
            </Stack>
          }
          color={promotionMap[selectedMembershipLevel].color}
          backgroundColor={promotionMap[selectedMembershipLevel].backgroundColor}
          className={`promotional-badge-${selectedMembershipLevel}`}
          displayMethod="mobileOnly"
          boxShadow="0 -1px 1px 0 rgba(8, 17, 34, 0.10), 0 0.5px 2px 0 rgba(8, 17, 34, 0.20)"
        />
      </Box>

      <Box className="benefit-list-container" marginBottom="1.5rem">
        <Stack className="benefit-detail-list" flexDirection="column" gap="0.5rem">
          {' '}
          {sectionListFragment}
        </Stack>
      </Box>
    </Stack>
  );
};

const supStyle = { fontSize: '0.8em', verticalAlign: 'text-bottom' } as const;

const getFullBenefitButtonText = (selectedMembershipLevel: MembershipLevel): React.ReactNode => {
  switch (selectedMembershipLevel) {
    case MembershipLevel.CLASSIC:
      return <span>See all Classic benefits</span>;
    case MembershipLevel.PLUS:
      return (
        <span>
          See all Plus<sup style={supStyle}>®</sup> benefits
        </span>
      );
    case MembershipLevel.PREMIER:
      return (
        <span>
          See all Premier<sup style={supStyle}>®</sup> benefits
        </span>
      );
    default:
      return <span>See all benefits</span>;
  }
};

export const getBenefitButtonText = (
  selectedMembershipLevel: MembershipLevel,
  isUpgrading: boolean,
  previousMembershipLevel: MembershipLevel,
  isMobile?: boolean
): React.ReactNode => {
  if (isUpgrading && previousMembershipLevel in MembershipLevel) {
    if (selectedMembershipLevel === previousMembershipLevel) {
      if (isMobile) return <span>Keep</span>;
      switch (selectedMembershipLevel) {
        case MembershipLevel.CLASSIC:
          return <span>Keep Classic</span>;
        case MembershipLevel.PLUS:
          return (
            <span>
              Keep Plus<sup style={supStyle}>®</sup>
            </span>
          );
        case MembershipLevel.PREMIER:
          return (
            <span>
              Keep Premier<sup style={supStyle}>®</sup>
            </span>
          );
      }
    }
    if (isMobile) return <span>Switch</span>;
    switch (selectedMembershipLevel) {
      case MembershipLevel.CLASSIC:
        return <span>Switch to Classic</span>;
      case MembershipLevel.PLUS:
        return (
          <span>
            Switch to Plus<sup style={supStyle}>®</sup>
          </span>
        );
      case MembershipLevel.PREMIER:
        return (
          <span>
            Switch to Premier<sup style={supStyle}>®</sup>
          </span>
        );
    }
  }
  if (isMobile) return <span>Join now</span>;
  switch (selectedMembershipLevel) {
    case MembershipLevel.CLASSIC:
      return <span>Join Classic</span>;
    case MembershipLevel.PLUS:
      return (
        <span>
          Join Plus<sup style={supStyle}>®</sup>
        </span>
      );
    case MembershipLevel.PREMIER:
      return (
        <span>
          Join Premier<sup style={supStyle}>®</sup>
        </span>
      );
    default:
      return <span>Join now</span>;
  }
};
